import { type ReactNode } from 'react';
import type { PrivateView, PublicView } from '@ringmaster/shared';
import { useRoom, type NarrationState } from '../net/useRoom.js';
import { GameHeader } from '../ui/GameHeader.js';
import { OneNightRolesButton } from '../ui/OneNightRolesButton.js';
import { themeForPhase } from '../theme/phaseTheme.js';
import {
  RoleRevealScreen,
  LobbyScreen,
  NightSleepScreen,
  NightWolfScreen,
  NightSeerScreen,
  DawnScreen,
  DiscussScreen,
  VoteScreen,
  DeadScreen,
  EndScreen,
  NarrationBanner,
} from './index.js';
import {
  WhisperDealScreen,
  WhisperQuestionScreen,
  WhisperHuntScreen,
  WhisperWaitScreen,
} from './WhisperScreens.js';
import {
  OneNightDealScreen,
  OneNightWolvesScreen,
  OneNightSeerScreen,
  OneNightSwapScreen,
  OneNightDawnScreen,
  OneNightResolveWaitScreen,
} from './OneNightScreens.js';

/**
 * HandGameView — the phase-routed private (per-phone) game view, shared by BOTH
 * the Play (player) route and the Host route so the host sees EXACTLY what a
 * player sees.
 *
 * Props:
 *  - showHeader: render the shared GameHeader (phase label + timer) at the top.
 *  - bottomSlot: extra fixed UI below the content (the host's RingmasterBar).
 *  - narration: rendered once as a NarrationBanner (never doubled with the header).
 *
 * The caller guarantees phase + publicView + privateView are present.
 */
export function HandGameView({
  publicView,
  privateView,
  narration,
  myName,
  showHeader = false,
  bottomSlot,
  onStartGame,
  onKickPlayer,
}: {
  publicView: PublicView;
  privateView: PrivateView;
  narration: NarrationState | null;
  myName: string;
  showHeader?: boolean;
  bottomSlot?: ReactNode;
  /** Seated-host start action for the LOBBY (Discord Activity mobile surface). */
  onStartGame?: () => void;
  /** Seated-host kick action for the LOBBY roster (same surface). */
  onKickPlayer?: (playerId: string) => void;
}) {
  const phase = publicView.phase;
  const phaseTheme = themeForPhase(phase);
  // Store state, not view state (net/sitting.ts): read at the routing layer so
  // EndScreen stays a pure function of its props — see ui/NextShowStrip.tsx.
  const sitting = useRoom((s) => s.sitting);

  // The seer reads their vision DURING NIGHT_SEER: the engine holds that phase
  // for SEER_REVEAL_HOLD seconds after the seer confirms (NightSeerScreen renders
  // the result). Once the night advances, the seer returns to the SAME screen as
  // everyone else — no reveal ever rides into DAWN/DISCUSS, where the table is
  // awake and could glance at the seer's phone.

  // When a host bottomSlot (RingmasterBar) is present, fixed action buttons must
  // sit ABOVE it — the same offset pattern the NarrationBanner uses.
  const actionOffset = bottomSlot ? 96 : 0;

  // The LOBBY has its own distinct identity design (room code / roster) and the
  // dead spectator view is self-contained, so the GameHeader only rides above the
  // active in-play phases (where phase label + timer are meaningful).
  const headerlessPhase = phase === 'LOBBY';
  const showTheHeader = showHeader && !headerlessPhase && privateView.alive;

  const header = showTheHeader ? (
    <GameHeader
      phase={phase}
      round={publicView.round}
      timerEndsAt={publicView.timerEndsAt}
      paused={publicView.paused}
      timerRemainingMs={publicView.timerRemainingMs}
      // Without this every hand surface labels phases as Blood Moon: the shared
      // LOBBY/DEAL phases read per game ("Preparation" in One Night, which has
      // no day count; "The séance opens" in The Whisper), and phaseLabel needs
      // the game to pick. It was missing, so both games' own DEAL copy existed
      // but never reached a phone.
      gameId={publicView.gameId}
    />
  ) : null;

  const { content, showBanner } = renderPhase();

  return (
    <div style={{ background: phaseTheme.bgGradient, minHeight: '100dvh', position: 'relative' }}>
      {header}
      {content}
      {/* One narration caption instance — suppressed when the phase screen
          already renders the caption itself (the sleep screen). Lifted above the
          host's RingmasterBar when a bottomSlot is present. */}
      {showBanner && <NarrationBanner narration={narration} offsetBottom={bottomSlot ? 96 : 0} />}
      {/* One Night only, and only once the cards exist: the persistent
          bottom-left roles button. Same offset idiom as the banner above — it
          renders itself only for a One Night room from DEAL onward. */}
      <OneNightRolesButton publicView={publicView} offsetBottom={bottomSlot ? 96 : 0} />
      {bottomSlot}
    </div>
  );

  function renderPhase(): { content: ReactNode; showBanner: boolean } {
    // ── The Whisper — route this game's own screens BEFORE the Blood Moon flow.
    //    (undefined gameId → 'bloodmoon', so Blood Moon is unaffected.) The
    //    Whisper has no mid-game death, so no DeadScreen branch — everyone plays
    //    every round; END shows the shared finale/leaderboard below.
    if (publicView.gameId === 'whisper' && phase !== 'LOBBY' && phase !== 'END') {
      return renderWhisperPhase();
    }

    // ── Blood Moon: One Night — route this game's own screens BEFORE the
    //    Blood Moon flow, same pattern as the Whisper above. Its only death
    //    (the simultaneous vote) lands at ONENIGHT_RESOLVE/END, where everyone
    //    — newly dead included — just watches the same rest/reveal beat, so
    //    there is no separate DeadScreen branch for this game.
    if (publicView.gameId === 'onenight' && phase !== 'LOBBY' && phase !== 'END') {
      return renderOneNightPhase();
    }

    // END outranks death: everyone (dead included) gets the finale + scoreboard,
    // and a dead host still needs the Rematch button.
    if (phase === 'END') {
      return {
        content: (
          <EndScreen
            privateView={privateView}
            publicView={publicView}
            actionOffset={actionOffset}
            sitting={sitting}
          />
        ),
        showBanner: true,
      };
    }

    // Dead players see the DeadScreen wrapper for every other phase.
    if (!privateView.alive) {
      return { content: <DeadScreen privateView={privateView} publicView={publicView} />, showBanner: true };
    }

    switch (phase) {

      case 'LOBBY':
        return {
          content: (
            <LobbyScreen
              publicView={publicView}
              myName={myName}
              myId={privateView.playerId}
              onStart={onStartGame}
              onKick={onKickPlayer}
            />
          ),
          showBanner: true,
        };

      case 'DEAL':
        return { content: <RoleRevealScreen privateView={privateView} actionOffset={actionOffset} />, showBanner: true };

      case 'NIGHT_INTRO':
      case 'NIGHT_WOLVES':
      case 'NIGHT_INTERLUDE':
      case 'NIGHT_SEER':
      case 'RESOLVE_NIGHT': {
        // Ambient pause beats (INTRO/INTERLUDE) have no actor → everyone sleeps.
        // During NIGHT_WOLVES only the wolf acts; during NIGHT_SEER only the seer.
        const actionType = privateView.action.type;
        if (phase === 'NIGHT_WOLVES' && actionType === 'WOLF_PICK') {
          return { content: <NightWolfScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };
        }
        if (phase === 'NIGHT_SEER' && actionType === 'SEER_PICK') {
          return { content: <NightSeerScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };
        }
        // Sleep screen renders the caption itself — don't double-render the banner.
        return { content: <NightSleepScreen caption={narration?.text} />, showBanner: false };
      }

      case 'DAWN':
        return { content: <DawnScreen publicView={publicView} />, showBanner: true };

      case 'DISCUSS':
        return { content: <DiscussScreen publicView={publicView} privateView={privateView} actionOffset={actionOffset} />, showBanner: true };

      case 'VOTE':
      case 'RESOLVE_VOTE':
        return { content: <VoteScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };

      default:
        return { content: null, showBanner: true };
    }
  }

  /** The Whisper's per-phase Hand screens (see hand/WhisperScreens.tsx). */
  function renderWhisperPhase(): { content: ReactNode; showBanner: boolean } {
    switch (phase) {
      case 'DEAL':
        // Roles + the Deceiver's word are dealt on DEAL ENTRY (engine) — full
        // press-and-hold card ceremony with the ready gate (reveal, then READY).
        return { content: <WhisperDealScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };
      case 'WHISPER_INTRO':
        // Pure ritual beat — the Seer's opening line over drifting wisps.
        return { content: <WhisperWaitScreen caption={narration?.text} title="The séance begins…" />, showBanner: false };
      case 'QUESTION':
        return {
          content: <WhisperQuestionScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />,
          showBanner: true,
        };
      case 'RESOLVE_GUESS':
        // Transient — never broadcast; kept for completeness.
        return { content: <WhisperWaitScreen caption={narration?.text} title="The Seer considers…" />, showBanner: false };
      case 'HUNT':
        return {
          content: <WhisperHuntScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />,
          showBanner: true,
        };
      case 'RESOLVE_HUNT':
        return { content: <WhisperWaitScreen caption={narration?.text} title="The mask falls…" />, showBanner: false };
      case 'SCORE':
        // Dead phase (engine never enters SCORE — one-round game, RESOLVE_HUNT →
        // END directly) — harmless fallback if ever reached.
        return { content: <WhisperWaitScreen caption={narration?.text} title="The Whisper" />, showBanner: false };
      default:
        return { content: <WhisperWaitScreen caption={narration?.text} title="The Whisper" />, showBanner: false };
    }
  }

  /**
   * Blood Moon: One Night's per-phase Hand screens (hand/OneNightScreens.tsx).
   * The sleep beats (ONENIGHT_NIGHTFALL, and every non-actor during a night
   * sub-phase), ONENIGHT_DISCUSS and ONENIGHT_VOTE reuse the EXISTING Blood
   * Moon screens unchanged — nothing about their contracts differs for this
   * game (see OneNightScreens.tsx's file doc comment).
   */
  function renderOneNightPhase(): { content: ReactNode; showBanner: boolean } {
    switch (phase) {
      case 'DEAL':
        return { content: <OneNightDealScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };

      case 'ONENIGHT_NIGHTFALL':
        // Pure ritual pause — nobody has an action yet.
        return { content: <NightSleepScreen caption={narration?.text} />, showBanner: false };

      case 'ONENIGHT_NIGHT_ACTIONS': {
        // EVERY night role — Werewolves, Seer, Thief AND Joker — wakes in this
        // ONE beat. Each player is dealt exactly one role, so at most one branch
        // below ever applies; everyone else sleeps through it. Both swappers
        // (Thief `picks === 1`, Joker `picks === 2`) land on the same
        // OneNightSwapScreen, which reads the shape off the action.
        if (privateView.oneNight?.packmateIds != null) {
          return { content: <OneNightWolvesScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };
        }
        const action = privateView.action;
        if (action.type === 'ONENIGHT_PEEK' && (action.candidates.length > 0 || action.centreAllowance > 0)) {
          return { content: <OneNightSeerScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };
        }
        if (action.type === 'ONENIGHT_SWAP') {
          return { content: <OneNightSwapScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };
        }
        return { content: <NightSleepScreen caption={narration?.text} />, showBanner: false };
      }

      case 'ONENIGHT_DAWN':
        return { content: <OneNightDawnScreen />, showBanner: true };

      case 'ONENIGHT_DISCUSS':
        return { content: <DiscussScreen publicView={publicView} privateView={privateView} actionOffset={actionOffset} />, showBanner: true };

      case 'ONENIGHT_VOTE':
        return { content: <VoteScreen privateView={privateView} publicView={publicView} actionOffset={actionOffset} />, showBanner: true };

      case 'ONENIGHT_RESOLVE':
        // The full truth (centre cards, dealt vs. final roles, who died) is
        // reserved for END — this is a calm holding beat, not a reveal.
        return { content: <OneNightResolveWaitScreen caption={narration?.text} />, showBanner: false };

      default:
        return { content: null, showBanner: true };
    }
  }
}
